import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './TreasuryStrategy.css';

const NMTCConsulting = () => {
  return (
    <>
      <Navbar />
      <main className="treasury-page" style={{ paddingTop: '80px' }}>
        {/* Two-column layout: Flowchart + Side cards */}
        <section className="treasury-main-section">
          <div className="treasury-container-wide">
            <div className="treasury-page-header">
              <Link to="/" className="treasury-back">&larr; Home</Link>
              <h1>NMTC Pipeline &amp; Deployment</h1>
              <p>
                Helping CDFIs and CDEs with NMTC allocation build deal pipeline,
                structure transactions, and deploy capital into qualified projects
              </p>
            </div>

            {/* LEFT: Pipeline-to-Deployment Flowchart */}
            <div className="treasury-col-flow">
              <h2>From Allocation to Impact</h2>

              <div className="flow">
                {/* Step 1 */}
                <div className="flow-step">
                  <div className="flow-icon">1</div>
                  <div className="flow-card step-thesis">
                    <h3>Pipeline Development &amp; Market Scanning</h3>
                    <p>
                      Proactively identifying <strong>NMTC-eligible projects</strong> in
                      your service area — screening census tracts, mapping community
                      needs, and building relationships with developers, school operators,
                      health systems, and non-profits who may not know NMTC is available
                      to them.
                    </p>
                    <div className="flow-tags">
                      <span>Census Tract Screening</span>
                      <span>Project Sourcing</span>
                      <span>Market Mapping</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 2 */}
                <div className="flow-step">
                  <div className="flow-icon">2</div>
                  <div className="flow-card step-firstloss">
                    <h3>Project Screening &amp; Eligibility</h3>
                    <p>
                      Evaluating potential QALICBs against your CDE's <strong>allocation
                      agreement, target markets, and impact criteria</strong> —
                      confirming census tract eligibility, QALICB status, project
                      readiness, and alignment with your deployment timeline.
                    </p>
                    <div className="flow-tags">
                      <span>QALICB Qualification</span>
                      <span>Impact Alignment</span>
                      <span>Readiness Assessment</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 3 */}
                <div className="flow-step">
                  <div className="flow-icon">3</div>
                  <div className="flow-card step-cdfi">
                    <h3>Deal Structuring &amp; Underwriting</h3>
                    <p>
                      Structuring the <strong>QLICI terms</strong> — loan amounts, rates,
                      tenors, collateral, and below-market features that make the deal
                      work for the project while meeting investor and compliance
                      requirements. Building credit memos and presenting to your
                      investment committee.
                    </p>
                    <div className="flow-tags">
                      <span>QLICI Structuring</span>
                      <span>Credit Analysis</span>
                      <span>Investment Committee</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 4 */}
                <div className="flow-step">
                  <div className="flow-icon">4</div>
                  <div className="flow-card step-fundraise">
                    <h3>Leverage &amp; Investor Coordination</h3>
                    <p>
                      Working with <strong>tax credit investors and leverage lenders</strong> to
                      finalize the capital stack — coordinating QEI structuring,
                      leverage loan terms, and investor due diligence to get to a
                      closeable transaction.
                    </p>
                    <div className="flow-tags">
                      <span>QEI Structuring</span>
                      <span>Leverage Lending</span>
                      <span>Investor Due Diligence</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 5 */}
                <div className="flow-step">
                  <div className="flow-icon">5</div>
                  <div className="flow-card step-close">
                    <h3>Closing &amp; Deployment</h3>
                    <p>
                      Managing the <strong>closing process</strong> — coordinating
                      legal counsel, tax counsel, investors, leverage lenders, and
                      QALICBs to execute on time and in compliance with your allocation
                      agreement and deployment deadlines.
                    </p>
                    <div className="flow-tags">
                      <span>Closing Coordination</span>
                      <span>Deployment Deadlines</span>
                      <span>Allocation Compliance</span>
                    </div>
                  </div>
                </div>

                <div className="flow-connector"><span className="flow-arrow">&#9660;</span></div>

                {/* Step 6 — Outcomes */}
                <div className="flow-step">
                  <div className="flow-icon outcome-icon">&#10003;</div>
                  <div className="flow-card step-outcomes">
                    <h3>Ongoing Monitoring &amp; Compliance</h3>
                    <div className="outcome-grid">
                      <div className="outcome-item">
                        <h4>Borrower Monitoring</h4>
                        <p>Financial performance tracking, construction milestones, and early warning for at-risk projects in your portfolio.</p>
                      </div>
                      <div className="outcome-item">
                        <h4>Compliance &amp; Reporting</h4>
                        <p>CIIS reporting, Substantially-All testing, and investor reporting throughout the 7-year compliance period.</p>
                      </div>
                      <div className="outcome-item">
                        <h4>Community Impact</h4>
                        <p>Tracking jobs created, services delivered, and community outcomes tied to your allocation commitments.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: NMTC Flow of Funds + Clarity's Role */}
            <div className="treasury-col-side">

              {/* NMTC Flow of Funds */}
              <div className="side-card">
                <h3>NMTC Flow of Funds</h3>
                <div className="cap-stack">
                  <div className="cap-layer cap-senior">
                    <div className="cap-label">Tax Credit Investor</div>
                    <div className="cap-pct-sm">Equity into<br/>Investment Fund</div>
                  </div>
                  <div className="cap-layer cap-bond">
                    <div className="cap-label">Leverage Lender</div>
                    <div className="cap-pct-sm">Loan to<br/>Investment Fund</div>
                  </div>
                  <div className="cap-layer cap-sub">
                    <div className="cap-label">CDE (Your Organization)</div>
                    <div className="cap-pct-sm">QEI from Fund<br/>&#8594; QLICIs out</div>
                  </div>
                  <div className="cap-layer cap-first">
                    <div className="cap-label">QALICB (Project)</div>
                    <div className="cap-pct-sm">Below-market<br/>capital received</div>
                  </div>
                </div>
                <p className="cap-note">
                  Simplified illustration. Actual structures vary by deal size, investor, and sub-CDE arrangements.
                </p>
              </div>

              {/* Where Clarity Fits In */}
              <div className="side-card clarity-role-card">
                <h3>Where Clarity Fits In</h3>
                <p className="clarity-role-intro">
                  We work alongside your CDE team to build and execute your
                  deployment strategy — not as a program manager, but as an
                  extension of your lending and origination capacity:
                </p>
                <div className="clarity-role-list">
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Pipeline Building</strong>
                      <p>Identifying eligible projects in your target markets that align with your allocation agreement and community impact goals.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Deal Underwriting</strong>
                      <p>Credit analysis, financial modeling, and credit memo preparation for QLICI transactions — charter schools, health clinics, community facilities.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Structuring Support</strong>
                      <p>Working through QLICI terms, leverage structures, and investor requirements to get deals to a closeable position.</p>
                    </div>
                  </div>
                  <div className="clarity-role-item">
                    <div className="clarity-role-bullet">&#9670;</div>
                    <div>
                      <strong>Deployment Strategy</strong>
                      <p>Helping you meet allocation deadlines with a realistic pipeline plan tied to your origination capacity and market conditions.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sector Underwriting Expertise */}
              <div className="side-card">
                <h3>Sector Underwriting Experience</h3>
                <p className="clarity-role-intro">
                  Deep underwriting experience in the project types that make up
                  the majority of NMTC transactions:
                </p>
                <div className="flow-tags" style={{ gap: '8px' }}>
                  <span style={{ padding: '6px 14px', fontSize: '0.82rem' }}>Charter Schools</span>
                  <span style={{ padding: '6px 14px', fontSize: '0.82rem' }}>Health Clinics / FQHCs</span>
                  <span style={{ padding: '6px 14px', fontSize: '0.82rem' }}>Community Facilities</span>
                  <span style={{ padding: '6px 14px', fontSize: '0.82rem' }}>Mixed-Use Development</span>
                  <span style={{ padding: '6px 14px', fontSize: '0.82rem' }}>Healthy Food / Grocery</span>
                  <span style={{ padding: '6px 14px', fontSize: '0.82rem' }}>Manufacturing</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* NMTC Deal Model Promo */}
        <section className="treasury-cta" style={{ background: 'linear-gradient(135deg, #1B4620 0%, #2d6a35 100%)' }}>
          <div className="treasury-container">
            <h2>Model Your NMTC Deal</h2>
            <p>
              Use our interactive NMTC Deal Model to size your transaction, see the full
              sources &amp; uses, and understand the 7-year structure — step by step.
            </p>
            <Link to="/nmtc-model" className="treasury-cta-btn">
              Open the NMTC Deal Model &rarr;
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="treasury-cta">
          <div className="treasury-container">
            <h2>Let's Deploy Your Allocation</h2>
            <p>
              If your CDE has allocation to deploy and needs origination and
              underwriting capacity, we can help you build pipeline and get deals closed.
            </p>
            <Link to="/" state={{ scrollTo: 'contact' }} className="treasury-cta-btn">
              Get in Touch &rarr;
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default NMTCConsulting;
